import React from 'react'
import type { ContextUploadItem } from '~/types/contextUpload'
import {
  CloseCircleIcon,
  getFileExtension,
  getFileNameWithoutExtension,
  CARD_STYLES,
  CARD_SIZES
} from './uploadCardShared'
import { getFileTypeIconByFilename, GenericFolderIcon } from '~/utils/fileTypeIcons'

export interface UploadCardProps {
  upload: ContextUploadItem
  onRemove: (id: number) => void
}

/**
 * Upload card for the chat input area.
 * Shows upload status/progress with a removable close button.
 * Figma specs: 236x64px, padding: 16px left, 8px right, 16px vertical, rounded 16px
 */
export const UploadCard: React.FC<UploadCardProps> = ({ upload, onRemove }) => {
  const isUploading = upload.uploadStatus === 'uploading'
  const isError = upload.uploadStatus === 'error'
  const isPending = upload.uploadStatus === 'pending'

  // Get file extension and display name (without extension for files)
  const isDirectory = upload.type === 'directory'
  const fileExtension = isDirectory ? 'folder' : getFileExtension(upload.name)
  const displayName = getFileNameWithoutExtension(upload.name, isDirectory)

  return (
    <div
      className='group relative flex items-center overflow-hidden'
      style={{
        width: `${CARD_SIZES.input.width}px`,
        height: `${CARD_SIZES.input.height}px`,
        borderRadius: CARD_STYLES.borderRadius,
        paddingLeft: '16px',
        paddingRight: '8px',
        paddingTop: '16px',
        paddingBottom: '16px',
        gap: '8px',
        background: isError ? CARD_STYLES.backgroundError : CARD_STYLES.background,
        boxShadow: CARD_STYLES.shadow
      }}
    >
      {/* Content: File name + progress/type info - grows to fill available space */}
      <div className='flex flex-col gap-1 min-w-0 flex-1 justify-center'>
        {/* File/Folder Name - with status prefix if uploading */}
        <div
          className='font-medium truncate overflow-hidden text-ellipsis whitespace-nowrap w-full'
          style={{
            fontSize: '12px',
            lineHeight: '16px',
            color: isError
              ? CARD_STYLES.textError
              : isUploading || isPending
                ? CARD_STYLES.textSecondary
                : CARD_STYLES.textPrimary
          }}
          title={upload.name}
        >
          {isUploading || isPending ? `Uploading - ${displayName}` : displayName}
        </div>

        {/* Bottom section: progress bar OR file type info */}
        <div className='w-full'>
          {isUploading || isPending ? (
            // Progress bar
            <div className='flex items-center gap-3 w-full'>
              <div
                className='flex-1 relative'
                style={{ height: '8px', paddingTop: '4px', paddingBottom: '4px' }}
              >
                {/* Trail (background) */}
                <div
                  className='absolute left-0 right-0 top-1/2 -translate-y-1/2'
                  style={{
                    height: '2px',
                    backgroundColor: CARD_STYLES.progressBarBg,
                    borderRadius: '1px'
                  }}
                />
                {/* Fill (progress) */}
                <div
                  className='absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-300 ease-out'
                  style={{
                    height: '2px',
                    width: `${upload.uploadProgress || 0}%`,
                    backgroundColor: CARD_STYLES.progressBarFill,
                    borderRadius: '1px'
                  }}
                />
              </div>
            </div>
          ) : isError ? (
            <div className='flex items-center gap-1'>
              <span
                className='truncate'
                style={{ fontSize: '12px', lineHeight: '16px', color: CARD_STYLES.textError }}
                title={upload.error}
              >
                {upload.error || 'Upload failed'}
              </span>
            </div>
          ) : (
            // File type info: icon + extension
            <div className='flex items-center gap-1'>
              <span
                className='flex items-center justify-center'
                style={{ width: '16px', height: '16px', color: CARD_STYLES.iconColor }}
              >
                {isDirectory ? (
                  <GenericFolderIcon />
                ) : (
                  (() => {
                    const IconComponent = getFileTypeIconByFilename(upload.name)
                    return <IconComponent />
                  })()
                )}
              </span>
              <span
                className='font-semibold'
                style={{
                  fontFamily: 'Geist Mono, monospace',
                  fontSize: '12px',
                  lineHeight: '100%',
                  color: CARD_STYLES.textSecondary
                }}
              >
                {fileExtension}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Close Button - 24x24 container with 18x18 icon */}
      <button
        onClick={() => onRemove(upload.id)}
        className={`
          flex items-center justify-center shrink-0
          text-[rgba(0,0,0,0.45)] hover:text-[rgba(0,0,0,0.65)]
          transition-colors duration-150
          ${isUploading || isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        `}
        style={{ width: '24px', height: '24px' }}
        aria-label={`Remove ${upload.name}`}
        disabled={isUploading || isPending}
      >
        <CloseCircleIcon />
      </button>
    </div>
  )
}

export default UploadCard
